import type { ColumnDef } from '@tanstack/react-table';
import { round } from '@/utils/mathUtils';
import { useEffect, useMemo, useState } from 'react';
import {
  Hash,
  Package,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  RotateCcw,
  Warehouse,
  CircleDollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Coins
} from 'lucide-react';

import { Image } from '@/components/custom-ui/Image';
import { Badge } from '@/components/ui/badge';
import { useAppTheme } from '@/theme/themeProvider';
import { DataTable } from '@/UI/TableShadcn';
import { formatNumberWithCommas } from '@/utils/FormatNumber';
import { cn } from '@/lib/utils';

import type { ICompanyItemGroupedInterface } from '../../api/CompanyItems.interface';
import ItemColumn from '../itemTable/ItemColumn';

type Props = {
  items: ICompanyItemGroupedInterface[] | undefined;
  allTypes: string[];
};

function GroupItemsTable({ items, allTypes }: Props) {
  const [visible, setVisible] = useState<string[]>([]);

  const { isDark } = useAppTheme();
  const columns = useMemo<ColumnDef<ICompanyItemGroupedInterface>[]>(
    () => [
      {
        accessorKey: 'ID',
        header: () => (
          <div className="flex items-center justify-center gap-1">
            <Hash className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold">#</span>
          </div>
        ),
        size: 50,
        cell: (row) => {
          return (
            <div className="flex w-full items-center justify-center">
              <Badge
                variant="outline"
                className={cn(
                  "h-7 w-10 justify-center font-mono text-xs font-medium",
                  "bg-gradient-to-br from-muted/50 to-muted/30",
                  "border-border/50"
                )}
              >
                {row.row.index + 1}
              </Badge>
            </div>
          );
        },
      },
      {
        accessorKey: 'ItemName',
        header: () => (
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" />
            <span className="font-semibold">الصنف</span>
          </div>
        ),
        size: 250,
        cell: (row) => {
          return (
            <ItemColumn
              itemID={row?.row?.original?.ItemID}
              itemName={row?.row?.original?.ItemName}
            />
          );
        },
      },
      {
        accessorKey: 'UnitPrice',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <CircleDollarSign className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            <span className="font-semibold">الجمهور</span>
          </div>
        ),
        size: 120,
        cell: (row) => {
          const price = round(row?.row?.original?.UnitPrice || 0, 2);
          return (
            <div className="flex items-center justify-center">
              <div className={cn(
                "flex items-center gap-1.5 rounded-lg px-3 py-2",
                "bg-gradient-to-r from-indigo-50 to-indigo-100/50",
                "dark:from-indigo-950/30 dark:to-indigo-900/20",
                "border border-indigo-200 dark:border-indigo-800",
                "shadow-sm"
              )}>
                <span className="font-mono text-sm font-bold text-indigo-700 dark:text-indigo-300">
                  {price || '-'}
                </span>
                <span className="text-xs text-indigo-600 dark:text-indigo-400">
                  ج.م
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'Stock',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <Warehouse className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="font-semibold">الرصيد</span>
          </div>
        ),
        size: 110,
        cell: (row) => {
          const stock = round(row?.row?.original?.Stock || 0, 2);
          const hasStock = stock > 0;
          return (
            <div className="flex items-center justify-center">
              <div className={cn(
                "flex items-center gap-1.5 rounded-lg px-3 py-2",
                "bg-gradient-to-r",
                hasStock 
                  ? "from-amber-50 to-amber-100/50 border-amber-200 dark:border-amber-800"
                  : "from-gray-50 to-gray-100/50 border-gray-200 dark:border-gray-800",
                hasStock
                  ? "dark:from-amber-950/30 dark:to-amber-900/20"
                  : "dark:from-gray-950/30 dark:to-gray-900/20",
                "border shadow-sm"
              )}>
                {hasStock && <Activity className="h-3 w-3 text-amber-600 dark:text-amber-400" />}
                <span className={cn(
                  "font-mono text-sm font-bold",
                  hasStock
                    ? "text-amber-700 dark:text-amber-300"
                    : "text-gray-500 dark:text-gray-400"
                )}>
                  {stock || '-'}
                </span>
              </div>
            </div>
          );
        },
      },

      {
        accessorKey: 'TotalPurchases',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <ArrowDownRight className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="font-semibold">الوارد</span>
          </div>
        ),
        size: 110,
        cell: (row) => {
          const purchases = round(row?.row?.original?.TotalPurchases || 0, 2);
          return (
            <div className="flex items-center justify-center">
              {purchases > 0 ? (
                <Badge className={cn(
                  "gap-1 font-mono text-xs font-bold",
                  "bg-gradient-to-r from-blue-500 to-blue-600 text-white",
                  "border-0 shadow-sm"
                )}>
                  <TrendingDown className="h-3 w-3" />
                  {purchases}
                </Badge>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </div>
          );
        },
      },

      {
        accessorKey: 'TotalReturnsSupplier',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <RotateCcw className="h-4 w-4 text-orange-600 dark:text-orange-400" />
            <span className="font-semibold">مرتجع م</span>
          </div>
        ),
        size: 110,
        cell: (row) => {
          const returns = round(row?.row?.original?.TotalReturnsSupplier || 0, 2);
          return (
            <div className="flex items-center justify-center">
              {returns > 0 ? (
                <Badge className={cn(
                  "gap-1 font-mono text-xs font-bold",
                  "bg-gradient-to-r from-orange-500 to-orange-600 text-white",
                  "border-0 shadow-sm"
                )}>
                  <RotateCcw className="h-3 w-3" />
                  {returns}
                </Badge>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'TotalSales',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <ShoppingCart className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <span className="font-semibold">مبيعات</span>
          </div>
        ),
        size: 110,
        cell: (row) => {
          const sales = round(row?.row?.original?.TotalSales || 0, 2);
          return (
            <div className="flex items-center justify-center">
              {sales > 0 ? (
                <Badge className={cn(
                  "gap-1 font-mono text-xs font-bold",
                  "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white",
                  "border-0 shadow-sm"
                )}>
                  <ArrowUpRight className="h-3 w-3" />
                  {sales}
                </Badge>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'TotalReturnsClient',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <RotateCcw className="h-4 w-4 text-red-600 dark:text-red-400" />
            <span className="font-semibold">مرتجع ع</span>
          </div>
        ),
        size: 110,
        cell: (row) => {
          const returns = round(row?.row?.original?.TotalReturnsClient || 0, 2);
          return (
            <div className="flex items-center justify-center">
              {returns > 0 ? (
                <Badge className={cn(
                  "gap-1 font-mono text-xs font-bold",
                  "bg-gradient-to-r from-red-500 to-red-600 text-white",
                  "border-0 shadow-sm"
                )}>
                  <RotateCcw className="h-3 w-3" />
                  {returns}
                </Badge>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'StockValue',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <Coins className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <span className="font-semibold">قيمة الرصيد</span>
          </div>
        ),
        size: 130,
        cell: (row) => {
          const value = formatNumberWithCommas(
            row?.row?.original?.StockValue || 0,
          );
          return (
            <div className="flex items-center justify-center">
              <div className={cn(
                "flex items-center gap-1.5 rounded-lg px-3 py-2",
                "bg-gradient-to-r from-purple-50 to-purple-100/50",
                "dark:from-purple-950/30 dark:to-purple-900/20",
                "border border-purple-200 dark:border-purple-800",
                "shadow-sm"
              )}>
                <TrendingUp className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                <span className="font-mono text-sm font-bold text-purple-700 dark:text-purple-300">
                  {value}
                </span>
                <span className="text-xs text-purple-600 dark:text-purple-400">
                  ج.م
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'SalesValue',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
            <span className="font-semibold">قيمة البيع</span>
          </div>
        ),
        size: 130,
        cell: (row) => {
          const value = formatNumberWithCommas(
            row?.row?.original?.SalesValue || 0,
          );
          return (
            <div className="flex items-center justify-center">
              <div className={cn(
                "flex items-center gap-1.5 rounded-lg px-3 py-2",
                "bg-gradient-to-r from-green-50 to-green-100/50",
                "dark:from-green-950/30 dark:to-green-900/20",
                "border border-green-200 dark:border-green-800",
                "shadow-sm"
              )}>
                <DollarSign className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                <span className="font-mono text-sm font-bold text-green-700 dark:text-green-300">
                  {value}
                </span>
                <span className="text-xs text-green-600 dark:text-green-400">
                  ج.م
                </span>
              </div>
            </div>
          );
        },
      },
    ],
    [], // end
  );

  useEffect(() => {
    if (allTypes.length === 0) return; // ✅ Skip if empty

    setVisible((prev) => {
      const updatedVisible = [...prev];

      if (
        !allTypes.includes(' بيع') &&
        !updatedVisible.includes('TotalSales')
      ) {
        updatedVisible.push('TotalSales', 'SalesValue');
      }
      if (
        !allTypes.includes('شراء') &&
        !updatedVisible.includes('TotalPurchases')
      ) {
        updatedVisible.push('TotalPurchases');
      }
      if (
        !allTypes.includes('مرتجع مورد') &&
        !updatedVisible.includes('TotalReturnsSupplier')
      ) {
        updatedVisible.push('TotalReturnsSupplier');
      }
      if (
        !allTypes.includes('مرتجع عميل') &&
        !updatedVisible.includes('TotalReturnsClient')
      ) {
        updatedVisible.push('TotalReturnsClient');
      }

      return updatedVisible;
    });
  }, [allTypes]);
  return (
    <div className="w-full h-full p-4 overflow-auto">
      {items && items.length > 0 ? (
        <div className="min-h-full rounded-xl border border-border/50 bg-background/50 backdrop-blur-sm">
          <DataTable
            columns={columns}
            data={items}
            isPaginated
            isTopToolbar
            isRowSelection
            visibleColumns={
              visible as unknown as (keyof ICompanyItemGroupedInterface)[]
            }
            heightTable="60vh"
          />
        </div>
      ) : (
        <div className="flex h-full flex-col items-center justify-center rounded-xl border border-border/30 bg-gradient-to-br from-muted/20 to-muted/10">
          <div className="flex flex-col items-center justify-center p-8">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/5 blur-3xl" />
              <Image
                alt="No data"
                src={
                  isDark ? '/images/svg/lost1.svg' : '/images/svg/lost.svg'
                }
                isBlurred
                className="relative h-[250px] w-[250px] opacity-80"
              />
            </div>
            <div className="mt-8 space-y-2 text-center">
              <h3 className="font-alex text-2xl text-muted-foreground">
                لا توجد حركات علي الشركة
              </h3>
              <p className="text-sm text-muted-foreground/60">
                لم يتم العثور على بيانات في المدة المحددة
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GroupItemsTable;
